// Temporarily disable drag and drop due to build issues
// import React, { useState, useEffect } from 'react';
// import {
//   DndContext,
//   closestCenter,
//   KeyboardSensor,
//   PointerSensor,
//   useSensor,
//   useSensors,
// } from '@dnd-kit/core';
// import {
//   arrayMove,
//   SortableContext,
//   sortableKeyboardCoordinates,
//   verticalListSortingStrategy,
// } from '@dnd-kit/sortable';
// import {
//   useSortable,
// } from '@dnd-kit/sortable';
// import { CSS } from '@dnd-kit/utilities';
// import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import React, { useState, useEffect } from 'react';
import VerseHighlighter from './VerseHighlighter';

function ContentArea({ studyGuide, updateStudyGuide, settings }) {
  const [editingSection, setEditingSection] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [verseHighlights, setVerseHighlights] = useState({});

  // Auto-save functionality
  useEffect(() => {
    if (settings?.autoSave && studyGuide.title && studyGuide.sections.length > 0) {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }

      const timeout = setTimeout(() => {
        // Auto-save logic would go here
        console.log('Auto-saving study guide...');
      }, 30000); // Auto-save after 30 seconds of inactivity

      setAutoSaveTimeout(timeout);
    }

    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [studyGuide, settings?.autoSave, autoSaveTimeout]);

  const updateSection = (sectionId, updates) => {
    const updatedSections = studyGuide.sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    );
    updateStudyGuide({ sections: updatedSections });
  };

  const deleteSection = (sectionId) => {
    const updatedSections = studyGuide.sections.filter(section => section.id !== sectionId);
    updateStudyGuide({ sections: updatedSections });
  };

  const moveSection = (sectionId, direction) => {
    const index = studyGuide.sections.findIndex(section => section.id === sectionId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= studyGuide.sections.length) return;

    const sections = [...studyGuide.sections];
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];

    updateStudyGuide({ sections });
  };

  // Drag and drop functionality temporarily disabled due to build issues
  // const handleDragEnd = (event) => {
  //   const { active, over } = event;
  //   if (active.id !== over.id) {
  //     const oldIndex = studyGuide.sections.findIndex((section) => section.id === active.id);
  //     const newIndex = studyGuide.sections.findIndex((section) => section.id === over.id);
  //     const newSections = arrayMove(studyGuide.sections, oldIndex, newIndex);
  //     updateStudyGuide({ sections: newSections });
  //   }
  // };

  const handleHighlight = (sectionId, selectedText, color) => {
    const sectionKey = `section-${sectionId}`;
    const currentHighlights = verseHighlights[sectionKey] || [];

    const newHighlight = {
      text: selectedText,
      color: color
    };

    setVerseHighlights(prev => ({
      ...prev,
      [sectionKey]: [...currentHighlights, newHighlight]
    }));

    // Update the section with highlights
    updateSection(sectionId, {
      highlights: [...(verseHighlights[sectionKey] || []), newHighlight]
    });
  };

  const renderVerseSection = (section) => (
    <div key={section.id} className="section verse-section">
      <div className="section-header">
        <h4>{section.book} {section.chapter}:{section.verse}</h4>
        <div className="section-actions">
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'up')}
            disabled={studyGuide.sections.indexOf(section) === 0}
            title="Move up"
          >
            ↑
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'down')}
            disabled={studyGuide.sections.indexOf(section) === studyGuide.sections.length - 1}
            title="Move down"
          >
            ↓
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => deleteSection(section.id)}
            title="Delete section"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="verse-display">
        <VerseHighlighter
          text={section.text}
          highlights={section.highlights || verseHighlights[`section-${section.id}`] || []}
          onHighlight={(selectedText, color) => handleHighlight(section.id, selectedText, color)}
        />
      </div>
      <div className="form-group">
        <label>Notes:</label>
        <textarea
          value={section.notes || ''}
          onChange={(e) => updateSection(section.id, { notes: e.target.value })}
          placeholder="Add your notes about this verse..."
          rows={3}
        />
      </div>
      <div className="verse-actions">
        <button
          className="btn btn-secondary btn-small"
          onClick={() => {
            const newSection = {
              id: Date.now(),
              type: 'notes',
              title: `Notes on ${section.book} ${section.chapter}:${section.verse}`,
              content: ''
            };
            updateStudyGuide({
              sections: [...studyGuide.sections, newSection]
            });
          }}
        >
          Add Notes Section
        </button>
        <button
          className="btn btn-secondary btn-small"
          onClick={() => {
            const newSection = {
              id: Date.now(),
              type: 'questions',
              title: `Questions on ${section.book} ${section.chapter}:${section.verse}`,
              questions: []
            };
            updateStudyGuide({
              sections: [...studyGuide.sections, newSection]
            });
          }}
        >
          Add Questions
        </button>
      </div>
    </div>
  );

  const renderNotesSection = (section) => (
    <div key={section.id} className="section notes-section">
      <div className="section-header">
        <input
          type="text"
          value={section.title}
          onChange={(e) => updateSection(section.id, { title: e.target.value })}
          className="section-title-input"
        />
        <div className="section-actions">
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'up')}
            disabled={studyGuide.sections.indexOf(section) === 0}
          >
            ↑
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'down')}
            disabled={studyGuide.sections.indexOf(section) === studyGuide.sections.length - 1}
          >
            ↓
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => deleteSection(section.id)}
          >
            ✕
          </button>
        </div>
      </div>
      <div className="form-group">
        <textarea
          value={section.content}
          onChange={(e) => updateSection(section.id, { content: e.target.value })}
          placeholder="Write your study notes here..."
          rows={6}
        />
      </div>
    </div>
  );

  const renderQuestionsSection = (section) => (
    <div key={section.id} className="section questions-section">
      <div className="section-header">
        <input
          type="text"
          value={section.title}
          onChange={(e) => updateSection(section.id, { title: e.target.value })}
          className="section-title-input"
        />
        <div className="section-actions">
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'up')}
            disabled={studyGuide.sections.indexOf(section) === 0}
          >
            ↑
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'down')}
            disabled={studyGuide.sections.indexOf(section) === studyGuide.sections.length - 1}
          >
            ↓
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => deleteSection(section.id)}
          >
            ✕
          </button>
        </div>
      </div>
      <div className="questions-list">
        {(section.questions || []).map((question, index) => (
          <div key={index} className="question-item">
            <input
              type="text"
              value={question}
              onChange={(e) => {
                const newQuestions = [...(section.questions || [])];
                newQuestions[index] = e.target.value;
                updateSection(section.id, { questions: newQuestions });
              }}
              placeholder={`Question ${index + 1}`}
            />
            <button
              className="btn btn-secondary btn-small"
              onClick={() => {
                const newQuestions = (section.questions || []).filter((_, i) => i !== index);
                updateSection(section.id, { questions: newQuestions });
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          className="btn btn-secondary"
          onClick={() => {
            const newQuestions = [...(section.questions || []), ''];
            updateSection(section.id, { questions: newQuestions });
          }}
        >
          Add Question
        </button>
      </div>
    </div>
  );

  const renderObservationsSection = (section) => (
    <div key={section.id} className="section observations-section">
      <div className="section-header">
        <input
          type="text"
          value={section.title}
          onChange={(e) => updateSection(section.id, { title: e.target.value })}
          className="section-title-input"
        />
        <div className="section-actions">
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'up')}
            disabled={studyGuide.sections.indexOf(section) === 0}
          >
            ↑
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => moveSection(section.id, 'down')}
            disabled={studyGuide.sections.indexOf(section) === studyGuide.sections.length - 1}
          >
            ↓
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => deleteSection(section.id)}
          >
            ✕
          </button>
        </div>
      </div>
      <div className="form-group">
        <textarea
          value={section.content}
          onChange={(e) => updateSection(section.id, { content: e.target.value })}
          placeholder="Record your observations and insights..."
          rows={6}
        />
      </div>
    </div>
  );

  return (
    <div className="content-area">
      <div className="study-guide-header">
        <input
          type="text"
          value={studyGuide.title}
          onChange={(e) => updateStudyGuide({ title: e.target.value })}
          placeholder="Study Guide Title"
          className="guide-title-input"
        />
      </div>

      <div className="sections-container">
        {studyGuide.sections.length === 0 ? (
          <div className="empty-state fade-in">
            <h3>Welcome to Bible Study Guide Creator</h3>
            <p>Get started by selecting verses from the sidebar or adding study sections.</p>
            <div className="quick-start">
              <h4>Quick Start:</h4>
              <ul>
                <li>Choose a Bible book and chapter from the sidebar</li>
                <li>Load verses and select the ones you want to study</li>
                <li>Add notes, questions, and observations for each verse</li>
                <li>Use templates to get started quickly</li>
                <li>Export your study guide when finished</li>
              </ul>
            </div>
          </div>
        ) : (
          studyGuide.sections.map((section, index) => {
            const sectionElement = (() => {
              switch (section.type) {
                case 'verse':
                  return renderVerseSection(section);
                case 'notes':
                  return renderNotesSection(section);
                case 'questions':
                  return renderQuestionsSection(section);
                case 'observations':
                  return renderObservationsSection(section);
                default:
                  return null;
              }
            })();

            return (
              <div key={section.id} className="section-wrapper slide-in" style={{ animationDelay: `${index * 0.1}s` }}>
                {sectionElement}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ContentArea;